/* eslint-disable no-nested-ternary */
import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Formik } from 'formik';
import debounce from 'lodash.debounce';
import { useSpring, animated } from 'react-spring';
import { Mentions } from 'antd';
import GiphyEmojiButtons from './GiphyEmojiButtons';
import SendButton from './SendButton';
import TBButton from '../../../../shared/components/Button/Button';
import feedSocket from '../../../../shared/utils/setFeedSocketConfig';

import {
  setIsReplyActivatedRedux,
  setUploadMediaFalse,
  setIsCommentClickedRedux,
} from '../../ducks/feedActions';
import useFeed from '../hooks/useFeed';
import {
  urlSnapper,
  userParser,
  buildSubmissionObject,
  findFeedIndex,
  filterUserMentions,
  replaceMentionsInMessage,
} from '../../helperFunctions';
import { feedTracker } from '../../../../shared/utils/amplitudeHelper';

const AnimatedMediaBoxDiv = styled(animated.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: 0;
  margin-left: 0px;
  left: 0px;
  min-height: 30px;
  min-width: 30px;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-image: ${(props) => `url(${props.image})`};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border: 0px solid #ced4da;
  border-radius: 50%;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

const TextActionsContainer = styled('div')`
  display: flex;
  align-items: center;
  position: absolute;
  right: 10px;
`;

const FormGroup = styled('div')`
  width: 100%;
  .MuiOutlinedInput-multiline {
    padding: 7px;
  }
  .MuiInputBase-multiline.MuiInputBase-marginDense {
    padding: 7px;
  }
  .MuiOutlinedInput-multiline.MuiOutlinedInput-marginDense {
    padding: 7px;
  }
  .MuiOutlinedInput-inputMarginDense {
    padding-left: 5px !important;
    margin-top: 2px !important;
  }
`;

const FormWrapper = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-bottom: 20px;
`;

const MediaBox = styled('div')`
  margin-right: ${(props) => (props.activeMediaBarOb[props.feedObjectId] && props.uploadMedia
    ? '15px'
    : '0px')};
  .remove-media {
    display: ${(props) => (props.uploadMedia ? 'flex' : 'none')};
    border-radius: 50%;
    height: 16px;
    width: 16px;
    position: absolute;
    left: 25px;
    z-index: 100;
    top: -5px;
    background: #d6d6d6;
  }
`;

const NewCommentBoxWrap = styled('div')`
  width: 100%;
  display: flex;
  padding-left: ${(props) => (props.activeMediaBarOb[props.feedObjectId] && props.uploadMedia
    ? '10px'
    : '20px')};
  padding-right: 20px;
`;

const NewCommentInput = styled('div')`
  display: flex;
  align-items: center;
  position: relative;
  .text-area {
    width: 100%;
    white-space: pre-line;
    padding-right: 100px;
    border-radius: 10px;
    padding: 4px 0px;
  }
`;

const NewCommentBox = ({ feedObject }) => {
  const { handleCreateFeedPostComment } = useFeed();

  const dispatch = useDispatch();
  const [userMentions, setUserMentions] = useState([]);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const uploadMedia = useSelector((state) => state.feed.data.uploadMedia);
  const feed = useSelector((state) => state.feed.data.feed);
  const pinnedFeed = useSelector((state) => state.feed.data.pinnedFeed);
  const users = useSelector((state) => state.feed.data.users);
  const activeMediaBarOb = useSelector(
    (state) => state.feed.data.activeMediaBarOb,
  );
  const isCommentClicked = useSelector(
    (state) => state.feed.data.isCommentClicked[feedObject.id],
  );

  const feedObjectId = feedObject.id || 0;
  const isTypingRef = useRef({
    postId: feedObject.id,
    accountCode: currentUser.accountCode,
    userId: currentUser.id,
  });

  const commentBox = useSpring({
    width: activeMediaBarOb[feedObjectId] && uploadMedia ? '90%' : '100%',
  });
  const mediaBoxStyle = useSpring({
    opacity: uploadMedia ? '1' : '0',
    minWidth: uploadMedia ? '30px' : '0px',
    maxWidth: uploadMedia ? '30px' : '0px',
  });

  let timer;
  const timeoutVal = 1000;

  const handleIsTyping = debounce(
    () => {
      feedSocket.emit('isTyping', {
        postId: feedObject.id,
        accountCode: currentUser.accountCode,
        userId: currentUser.id,
        isTyping: true,
      });
    },
    timeoutVal,
    {
      leading: true,
      trailing: false,
    },
  );

  // When the user has stopped pressing on keys, set the timeout
  // if the user presses on keys before the timeout is reached, then this timeout is canceled
  const handleKeyUp = useCallback(() => {
    if (timer) {
      window.clearTimeout(timer); // prevent errant multiple timeouts from being generated
    }
    timer = window.setTimeout(() => {
      feedSocket.emit('isTyping', {
        postId: isTypingRef.current.postId,
        accountCode: isTypingRef.current.accountCode,
        userId: isTypingRef.userId,
        isTyping: false,
      });
    }, timeoutVal);
  }, [timeoutVal]);

  // We setup listeners for only keyup since we use onChange for keydown.
  useEffect(() => {
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keyup', handleKeyUp);
      if (timer) {
        window.clearTimeout(timer);
      }
    };
  }, [handleKeyUp]);

  useEffect(() => {
    if (isCommentClicked) {
      const selector = document.getElementById(`${feedObject.id}`);
      if (selector) {
        selector.focus({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }
  }, [isCommentClicked]);

  const submitDisabled = (messageBody) => !uploadMedia.id && !messageBody?.trim().length;

  const handleBlurComment = () => {
    dispatch(setIsCommentClickedRedux({}));
  };

  const handleMentionsChange = (setFieldValue, string) => {
    setFieldValue('messageBody', string);
    filterUserMentions(userMentions, setUserMentions, string);
    handleIsTyping();
  };

  const handleMentionsFocus = () => {
    dispatch(setIsReplyActivatedRedux({}));
  };

  const handleMentionsKeyPress = (e, submitForm) => {
    const { key, shiftKey } = e;
    if (key === 'Enter' && !shiftKey) {
      e.preventDefault();
      submitForm();
    }
  };

  const handleMentionsSelect = (selection) => {
    if (selection && selection.value) {
      // Find the user that matches the selected value
      const selectedUser = users.find((user) => `${user.first} ${user.last}` === selection.value);

      if (selectedUser) {
        const submissionValue = `[u=${selectedUser.id}|${selectedUser.first} ${selectedUser.last}]`;
        setUserMentions((prevUserMentions) => [
          ...prevUserMentions,
          submissionValue,
        ]);
      }
    }
  };

  const handleRemoveMedia = () => {
    dispatch(setUploadMediaFalse());
  };

  const handleSendClick = (values, submitForm, setSubmitting) => {
    setSubmitting(true);
    submitForm(values);
  };

  const handleSubmit = (values) => {
    if (submitDisabled(values.messageBody)) return;
    const { messageBody } = values;
    const processedText = messageBody
      ? replaceMentionsInMessage(messageBody, userMentions)
      : '';
    const submissionOb = buildSubmissionObject(processedText, uploadMedia);

    const feedObjectIndex = findFeedIndex(feedObject, feed, pinnedFeed);

    handleCreateFeedPostComment({
      accountCode: currentUser.accountCode,
      submissionOb,
      feedObjectIndex,
      feedObjectId: feedObject.id,
      pinned: feedObject.pinned,
    });
    feedTracker('Normal Mode', 'Comment created');
  };

  const renderMediaBox = () => (
    <MediaBox
      activeMediaBarOb={activeMediaBarOb}
      feedObjectId={feedObjectId}
      uploadMedia={uploadMedia}
      image={urlSnapper(uploadMedia)}
    >
      <AnimatedMediaBoxDiv
        style={mediaBoxStyle}
        image={urlSnapper(uploadMedia)}
      >
        <TBButton
          icon='remove'
          iconOnly
          square
          onClick={handleRemoveMedia}
          className='remove-media'
        />
      </AnimatedMediaBoxDiv>
    </MediaBox>
  );

  const renderMentionsInput = (
    values,
    isSubmitting,
    submitForm,
    setFieldValue,
  ) => (
    <Mentions
      className='text-area'
      placement='bottom'
      placeholder={
        feedObject.replyAllowed ? 'Reply to post' : 'Comments Disabled'
      }
      value={values.messageBody}
      onFocus={handleMentionsFocus}
      onSelect={handleMentionsSelect}
      disabled={isSubmitting || !feedObject.replyAllowed}
      id={feedObject ? feedObject.id : ''}
      name='messageBody'
      onKeyDown={(e) => handleMentionsKeyPress(e, submitForm)}
      onChange={(string) => handleMentionsChange(setFieldValue, string)}
    >
      {users && !!users.length && userParser(users)}
    </Mentions>
  );

  const renderTextActions = (
    values,
    setSubmitting,
    submitForm,
    setFieldValue,
  ) => (
    <TextActionsContainer>
      <GiphyEmojiButtons
        setFieldValue={setFieldValue}
        values={values}
        feedPostId={feedObject.id}
        fieldName='messageBody'
      />
      <SendButton
        onClick={() => handleSendClick(values, submitForm, setSubmitting)}
        disabled={submitDisabled(values.messageBody)}
      />
    </TextActionsContainer>
  );

  const initialValues = {
    messageBody: '',
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        handleSubmit(values);
        setUploadMediaFalse();
        resetForm();
        setSubmitting(false);
      }}
    >
      {({
        submitForm,
        setFieldValue,
        values,
        setSubmitting,
        isSubmitting,
      }) => (
        <FormWrapper onBlur={handleBlurComment}>
          {feedObject && activeMediaBarOb[feedObjectId] && renderMediaBox()}
          <animated.div style={commentBox}>
            <NewCommentBoxWrap
              activeMediaBarOb={activeMediaBarOb}
              feedObjectId={feedObjectId}
              uploadMedia={uploadMedia}
            >
              <FormGroup>
                <NewCommentInput>
                  {renderMentionsInput(
                    values,
                    isSubmitting,
                    submitForm,
                    setFieldValue,
                  )}
                  {feedObject.replyAllowed
                    && renderTextActions(
                      values,
                      setSubmitting,
                      submitForm,
                      setFieldValue,
                    )}
                </NewCommentInput>
              </FormGroup>
            </NewCommentBoxWrap>
          </animated.div>
        </FormWrapper>
      )}
    </Formik>
  );
};

NewCommentBox.propTypes = {
  feedObject: PropTypes.instanceOf(Object).isRequired,
};

export default NewCommentBox;
